import React, { useContext } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getUserEmail } from '../utils/authUser';

const PRODUCT_LOGO_URL = 'https://d7umqicpi7263.cloudfront.net/img/product/2b18747e-3ac1-44a0-9139-651800f162e8.com/895833868c0d761a640f9b58119d927f';

function resolveEnvironmentLabel(environment, awsAccountId) {
  const labelsByAccount = {
    '129096879518': 'DEV',
    '545314153384': 'SELLER-TEST',
  };

  return labelsByAccount[awsAccountId] || environment;
}

function Layout() {
  const {
    user,
    signOut,
    customerOptions,
    activeCustomerId,
    customerScopeLoading,
    selectCustomerId
  } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const userEmail = getUserEmail(user);
  const userInitial = userEmail.charAt(0).toUpperCase();
  const environment = (process.env.REACT_APP_ENVIRONMENT || 'unknown').toUpperCase();
  const awsAccountId = process.env.REACT_APP_AWS_ACCOUNT_ID || 'unknown';
  const environmentLabel = resolveEnvironmentLabel(environment, awsAccountId);
  const handleCustomerChange = (event) => {
    const customerId = event.target.value;
    if (customerId === activeCustomerId) {
      return;
    }

    selectCustomerId(customerId);

    if (location.pathname.startsWith('/instances/')) {
      navigate('/instances');
    }
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img className="brand-logo" src={PRODUCT_LOGO_URL} alt="Virtuoso product logo" />
          <h1>Virtuoso<span>SaaS</span></h1>
        </div>

        <div className="environment-badge">
          <div className="environment-badge-title">Control Plane</div>
          <div className="environment-badge-row">
            <span>Env</span>
            <strong>{environmentLabel}</strong>
          </div>
          <div className="environment-badge-row">
            <span>AWS</span>
            <code>{awsAccountId}</code>
          </div>
        </div>

        <div className="tenant-switcher">
          <label htmlFor="customer-selector">Customer</label>
          <select
            id="customer-selector"
            value={activeCustomerId || ''}
            onChange={handleCustomerChange}
            disabled={customerScopeLoading || customerOptions.length <= 1}
          >
            {customerOptions.length === 0 && (
              <option value="">{customerScopeLoading ? 'Loading customers...' : 'No customer linked'}</option>
            )}
            {customerOptions.map((customer) => {
              const customerId = customer.customer_id || '';
              const suffix = customer.email ? ` - ${customer.email}` : '';
              return (
                <option key={customerId} value={customerId}>
                  {customerId}{suffix}
                </option>
              );
            })}
          </select>
        </div>

        <nav>
          <ul className="sidebar-nav">
            <li>
              <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                Dashboard
              </NavLink>
            </li>
            <li>
              <NavLink to="/instances" className={({ isActive }) => isActive ? 'active' : ''}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                Instances
              </NavLink>
            </li>
            <li>
              <NavLink to="/clusters" className={({ isActive }) => isActive ? 'active' : ''}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" /><line x1="10" y1="7" x2="7" y2="17" /><line x1="14" y1="7" x2="17" y2="17" /></svg>
                RDF Clusters
              </NavLink>
            </li>
            <li>
              <NavLink to="/usage" className={({ isActive }) => isActive ? 'active' : ''}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="20" x2="12" y2="10" />
                  <line x1="18" y1="20" x2="18" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="16" />
                </svg>
                Usage & Billing
              </NavLink>
            </li>
            <li>
              <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Settings
              </NavLink>
            </li>
          </ul>
        </nav>

        <div className="user-menu">
          <div className="user-menu-info">
            <div className="user-avatar">{userInitial}</div>
            <div>
              <div className="user-name">{userEmail.split('@')[0]}</div>
              <div className="user-email">{userEmail}</div>
            </div>
          </div>
          <button onClick={signOut} className="btn btn-secondary" style={{ width: '100%' }}>
            Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet key={activeCustomerId || 'no-customer'} />
      </main>
    </div>
  );
}

export default Layout;
